import React, { useState, useEffect, useContext, forwardRef, useImperativeHandle, useMemo, useCallback, useRef } from 'react';
import { Box, Typography, Fab, Button, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import { useLocation, useHistory } from 'react-router-dom';
import AuthContext from "./auth/Context";
import AddIcon from '@mui/icons-material/Add';

import { useMutation, useInfiniteQuery, useQueryClient, useQuery } from '@tanstack/react-query';

import DataListHeader from './DataListHeader';
import DataListQuickFilter from './DataListQuickFilter';
import DataListFilter from './DataListFilter';
import DataListItems from './DataListItems';
import DataView from './DataView';
import DataListForm from './DataListForm';
import useScrollRestoration from './useScrollRestoration';
import { buildDateCondition } from './DateFilterInput';
import LockScreen from "./LockScreen";
import useAuthGate from "./useAuthGate";
import PrintActionDrawer from './PrintActionDrawer';
import DataListActionBar from './DataListActionBar';
import DataListActions from './DataListActions';
import DataRowActions from './DataRowActions';
import { taoGoiRoute, truongPhaiDoc } from './nutChucNangDanhSach';
import useContainerQuery from './useContainerQuery';
import { useQuickAction } from './DataQuickActionContext';
import { buildSavePayload, coerceFieldValue } from './formRules';
import { buildHookArgs, interpretHookResult } from './lifecycleHooks';
import { canInlineEdit, resolveEditableInList, setDraftValue, clearDraft, draftedRow,
         pendingChanges, dirtyRowIds as computeDirtyRowIds } from './inlineEdit';
import { deepEqual } from './formRules';
import { listinfoHeaders, resolveListinfoCode } from './listinfoBinding';
import { normalizeQuickFilters, initialQuickFilterValues, quickFilterValue } from './listinfoProps';
import { quickFilterCondition, combineConditions, syncQuickFiltersFromCondition } from './quickFilterRules';
import { prepareSearchFields, buildSearchCondition } from './searchFormRules';
import DataListSearchForm from './DataListSearchForm';
import { duongDanHuongDan, tieuDeHuongDan } from './huongDanRules';
import * as utils from '../utils';
import Moment from 'moment';
import { resolveFieldDefaults } from './fieldDefaults';
import { boThongTinKhiSaoChep } from './saoChepBanGhi';
import SplitPane from './SplitPane';
import RowDetailTabs from './RowDetailTabs';
import { shouldShowDetailPane, resolveActiveRowId, readSplitRatio, writeSplitRatio, DEFAULT_SPLIT_RATIO } from './masterDetailSplit';

const IOS_BG = '#f2f2f7';

// Form sửa nhanh cấu hình màn hình — nạp TRỄ: DataListConfigManager import ngược lại DataListManager.
const DataListConfigEditor = React.lazy(() => import('./DataListConfigManager').then(m => ({ default: m.DataListConfigEditor })));
// Hộp "Hướng dẫn" (Markdown) — nạp TRỄ: kéo theo react-markdown, phần lớn màn hình không có file hướng dẫn.
const HuongDanDialog = React.lazy(() => import('./HuongDanDialog'));

// Thư viện tiêm cho script `default` (fieldDefaults.js cố ý không import gì để test node chạy được
// trên chính mã nguồn). Hằng ở cấp module -> identity không đổi giữa các lần render.
const DEFAULT_SCRIPT_DEPS = { Moment, utils };

const getCleanSearchString = (search) => {
  if (!search) return "";
  const params = new URLSearchParams(search);
  const keysToDelete = [];
  for (const key of params.keys()) {
      if (key.startsWith('dialog_')) keysToDelete.push(key);
  }
  keysToDelete.forEach(key => params.delete(key));
  const newSearch = params.toString();
  return newSearch ? `?${newSearch}` : "";
};

const DataListManagerRender = forwardRef((props, ref) => {
  const {
    location,
    title = "Quản lý dữ liệu", code, apiCode, condition = {}, limit = 20, 
    sortOptions = [{ label: "Mặc định", sort: { stt: 1 } },{ label: "Mới nhất", sort: { date_created: -1 } }, { label: "Cũ nhất", sort: { date_created: 1 } }],
    fields = [], quickFilterConfig = null, quickFilterConfigs = null, defaultValues = {}, banners = null,
    
    // `listActions`: NÚT CHỨC NĂNG RIÊNG của danh sách này (khai trong data_list_config) — vd "Đồng bộ
    // bài viết" ở danh sách bài post. Vẽ trên thanh tiêu đề, xem DataListActions.jsx.
    listActions = [],
    // `rowActions`: NÚT TUỲ CHỈNH CỦA TỪNG DÒNG — cột "Thao tác" (bảng / thẻ / gọn) + màn hình xem. Vd lead: "Tạo cơ hội",
    // "Tạo đơn hàng". Xem DataRowActions.jsx.
    rowActions = [],
    actionBarPosition = 'bottom', headerRight, canAdd = false, canEdit = false, canDelete = false, canCopy = true, 
    
    hideHeader = false, containerSx = {}, listContainerSx = {}, gridProps = { xs: 12, sm: 6, md: 4, lg: 3 },
    transformData, onSaveSuccess, onAdd, onEdit, onSave, onBack, renderView, requireLogin = false,
    headless = false, headlessConfig = null, onHeadlessClose = () => {}, formSize, 
    defaultViewMode,
    renderItem,
    customFormScript,
    customListScript,
    customViewScript,
    checkBeforeSave,   // script kiểm tra trước khi lưu (cấp form) — xem formRules.interpretCheckResult
    // BỘ SCRIPT VÒNG ĐỜI (tương đương on_saved / on_deleting / on_deleted / on_init / on_init_update /
    // handle_cell_clicked của listinfo). Đã được parseDataListConfig biên dịch thành async function với
    // tên tham số lấy từ lifecycleHooks.HOOK_ARGS. Xem runHook bên dưới.
    onInit, onInitUpdate, onSaved, onDeleting, onDeleted, handleCellClicked,
    masterData,        // dữ liệu form MẸ khi màn hình này nằm trong một form khác (nếu có)
    searchRequired,    // bắt nhập điều kiện trước khi tải danh sách (bảng nhiều năm dữ liệu)
    changeCodeForm,    // mã form "Đổi mã" (tương đương listinfo.options.change_code_form); rỗng -> ẩn mục đó
    // HÀNH VI SERVER: màn hình này mượn xử lý riêng (phân quyền / trạng thái / hook / ghi sổ) của listinfo
    // nào. Xem listinfoBinding.js. Không khai -> lùi về `code`; `useStandardApi` -> không mượn của ai.
    listinfoCode, useStandardApi,
    // Tự tách nhóm ở phần master của bố cục chứng từ. Bỏ trống = bật; cấu hình dịch từ listinfo đặt false
    // để giữ nguyên bố cục field vốn có của listinfo.
    autoGroupMaster,
    showDetailOnGrid,  // chia đôi màn hình danh sách (trên: bảng, dưới: chi tiết dòng đang chọn).
                       // Bỏ trống = TỰ BẬT khi có lưới chi tiết; khai false để tắt.
    codeFormOption, // mã form tuỳ chọn để mở qua openEdit; mặc định "options"
    // FORM TÌM KIẾM RIÊNG (← search_form của listinfo): `searchFields` khai thẳng thắng; không có thì nạp
    // field của màn hình mã `searchForm`. Có form này thì nút Lọc mở form tìm kiếm. Xem searchFormRules.js.
    searchForm, searchFields,
    // Các TAB của trang xem (← view_tabs của listinfo) — chuyển thẳng xuống DataView.
    viewTabs,
    // Nút "Cấu hình màn hình" trên thanh tiêu đề. Bỏ trống = hiện cho quản trị (hệ thống hoặc công ty);
    // `false` để ẩn hẳn.
    canConfigure,
    // FILE HƯỚNG DẪN (Markdown tĩnh ở <server_url>/docs/<tên file>, vd "so1-don-hang-guide.md").
    // Có khai thì thanh tiêu đề có nút "Hướng dẫn" — xem huongDanRules.js / HuongDanDialog.jsx.
    guideFile,
  } = props;

  const context = useContext(AuthContext);
  const quickAction = useQuickAction();

  // MỘT chỗ duy nhất quyết định màn hình này mượn hành vi server của listinfo nào (xem listinfoBinding.js).
  // Mọi lời gọi API bên dưới đều dùng `apiHeaders` — không nơi nào được tự dựng header này.
  const listinfoBindingCfg = { listinfoCode, useStandardApi, moduleCode: code };
  const apiHeaders = listinfoHeaders(listinfoBindingCfg);
  const boundListinfoCode = resolveListinfoCode(listinfoBindingCfg);
  const { containerRef, isMobile, isSmUp } = useContainerQuery();

  const history = useHistory();
  //const theme = useTheme();
  //const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  //const isSmUp = useMediaQuery(theme.breakpoints.up('md'));

  const queryClient = useQueryClient(); 
  
  const skipUrlEffectRef = useRef(false);

  // =====================================================================
  // KIỂM TRA QUYỀN SỞ HỮU URL DỰA VÀO ĐƯỜNG DẪN (/page/list/:code)
  // =====================================================================
  const isMainUrlList = useMemo(() => {
    if (!location.pathname || !code) return false;
    const pathSegments = location.pathname.split('/').filter(Boolean);
    // So sánh code của list hiện tại với segment cuối cùng của URL
    return pathSegments.length > 0 && pathSegments[pathSegments.length - 1] === code;
  }, [location.pathname, code]);

  const isPublicUser = !context.userInfo || context.userInfo?.email === 'public';
  const userToken = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '') || context.config?.public_token;
  // Trạng thái chắn màn hình + có được phép gọi API chưa (xem ./useAuthGate).
  const authReady = useAuthGate(requireLogin);

  const cleanSearch = useMemo(() => getCleanSearchString(location.search), [location.search]);

  const [currentSort, setCurrentSort] = useState(sortOptions[0] || { label: "Mặc định", sort: { stt: 1 } });
  const [formOpen, setFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentEditItem, setCurrentEditItem] = useState(null);
  const [viewId, setViewId] = useState(null); 
  const [dynamicRedirect, setDynamicRedirect] = useState(null);
  const [quickSearch, setQuickSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(''); 
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  // BỘ LỌC NHANH — NHIỀU HÀNG. InputBase cho khai `filterQuickConfigs` (mảng): chứng từ thường lọc theo
  // cả kho lẫn trạng thái. Khoá số ít `quickFilterConfig` vẫn nhận, được gộp vào cùng danh sách để chỉ
  // có MỘT nguồn sự thật.
  const quickFilters = useMemo(
    () => normalizeQuickFilters(quickFilterConfigs, quickFilterConfig),
    [quickFilterConfigs, quickFilterConfig]
  );
  const [activeQuickFilters, setActiveQuickFilters] = useState(() => initialQuickFilterValues(quickFilters));
  // Cấu hình đổi (đổi màn hình, props chạy xong) -> nạp lại giá trị mặc định của các hàng lọc.
  const quickFilterKey = useMemo(() => quickFilters.map(q => q.field).join('|'), [quickFilters]);
  const prevQuickKeyRef = useRef(quickFilterKey);
  useEffect(() => {
    if (prevQuickKeyRef.current === quickFilterKey) return;
    prevQuickKeyRef.current = quickFilterKey;
    setActiveQuickFilters(initialQuickFilterValues(quickFilters));
  }, [quickFilterKey, quickFilters]);
  const [appliedDateFilter, setAppliedDateFilter] = useState(null); 
  // FORM TÌM KIẾM RIÊNG: giá trị người dùng nhập (để mở lại form thấy đúng) + điều kiện đã dịch.
  const [searchFormOpen, setSearchFormOpen] = useState(false);
  const [searchFormValues, setSearchFormValues] = useState(null);
  const [searchFormCondition, setSearchFormCondition] = useState(null);
  
  const [currentLimit, setCurrentLimit] = useState(limit || 20);

  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]); 
  const [showPrintDialog, setShowPrintDialog] = useState(false); 

  const finalApiCode = apiCode || code;

  // Giá trị "Tuỳ chọn mặc định" (options.option) của module — dùng để tự điền khi TẠO MỚI.
  // Cache bằng react-query (staleTime Infinity) nên KHÔNG tải lại mỗi lần mở form.
  const optionsQueryKey = useMemo(
    () => [context.config.id_app, 'options_defaults', (code || '').toLowerCase()],
    [context.config.id_app, code]
  );
  const { data: optionDefaults } = useQuery({
    queryKey: optionsQueryKey,
    queryFn: async () => {
      const idFunc = (code || '').toLowerCase();
      const opt = await context.apis.asyncGetData(userToken, 'options', { id_func: idFunc });
      return (opt && opt.option && typeof opt.option === 'object') ? opt.option : {};
    },
    enabled: !!code && !!userToken,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Mở form "Tuỳ chọn" (mô phỏng OptionsForm.js cũ, nhưng lấy cấu hình field từ `data_list_config`
  // thay cho `listinfo`):
  //  1) fetch bản ghi options của module (singleton theo id_func) làm itemData — dữ liệu nằm ở `option.*`;
  //  2) dựng các field "Giá trị mặc định" từ chính field của module (data_list_config): chỉ lấy các
  //     kiểu cơ bản, bỏ textarea/so_ct/user/password; mỗi field trỏ vào `option.<field>`;
  //  3) openEdit merge fields riêng của form Tuỳ chọn (codeFormOption) với các field vừa dựng.
  const handleOpenOptions = useCallback(async () => {
    if (!quickAction?.openEdit) return;
    const optionFormCode = codeFormOption || 'options';
    const idFunc = (code || finalApiCode || '').toLowerCase();

    // (1) itemData
    let itemData = { id_func: idFunc, option: {} };
    try {
      const opt = await context.apis.asyncGetData(userToken, 'options', { id_func: idFunc });
      if (opt) itemData = opt;
    } catch (e) {
      console.error('[DataListManager] lỗi tải options:', e?.message || e);
    }
    if (!itemData.option) itemData.option = {};

    // (2) fields "Giá trị mặc định" lấy từ data_list_config của module hiện tại
    const ALLOWED_TYPES = ['String', 'Number', 'Boolean', 'Date', 'DateTime', 'Time'];
    const extraFields = [];
    try {
      const res = await context.apis.asyncGetList(userToken, 'data_list_config', { condition: { moduleCode: code }, limit: 1 });
      const cfg = res?.data?.[0] || res?.[0];
      const rawFields = cfg?.fields || [];
      rawFields.forEach((f) => {
        const name = f.field || f.name;
        if (!name || ALLOWED_TYPES.indexOf(f.type) < 0) return;
        if (f.multiline || name === 'so_ct' || name.indexOf('user') >= 0 || name.indexOf('password') >= 0) return;
        extraFields.push({
          field: `option.${name}`, // sửa vào bản ghi options ở nhánh option.<field>
          label: f.label || name,
          type: f.type,
          required: false,
          default: '',
          gridEdit: { xs: 12, sm: 6, md: 3 }
        });
      });
    } catch (e) {
      console.error('[DataListManager] lỗi tải data_list_config cho options:', e?.message || e);
    }

    // (3) openEdit: fields = (fields của form codeFormOption) + extraFields
    quickAction.openEdit(optionFormCode, undefined, undefined, itemData, () => {
      if (refetchList) refetchList();
      // Options vừa đổi → xoá cache để lần tạo mới sau lấy giá trị mặc định mới
      queryClient.invalidateQueries({ queryKey: optionsQueryKey });
    }, undefined, extraFields);
  }, [quickAction, codeFormOption, code, finalApiCode, context.apis, userToken, queryClient, optionsQueryKey]);

  // ===== MÀN HÌNH CHIA ĐÔI: dòng đang xem + tỉ lệ chia (nhớ theo từng màn hình) =====
  const [activeRowId, setActiveRowId] = useState(null);
  const [splitRatio, setSplitRatio] = useState(() => {
    try { return readSplitRatio(window.localStorage, code); } catch (e) { return DEFAULT_SPLIT_RATIO; }
  });
  const handleRatioCommit = useCallback((r) => {
    try { writeSplitRatio(window.localStorage, code, r); } catch (e) { /* storage hỏng: bỏ qua */ }
  }, [code]);

  // 1. State lưu trữ quyết định chủ động của người dùng (nếu họ bấm đổi view)
  const [manualViewMode, setManualViewMode] = useState(null);
  // 2. Tự động suy luận viewMode dựa trên thời gian thực
  const viewMode = useMemo(() => {
    // Ưu tiên 1: Người dùng đã tự tay bấm nút đổi chế độ xem -> Luôn tôn trọng lựa chọn này
    if (manualViewMode) return manualViewMode;
    // Ưu tiên 2: Nếu developer truyền vào hàm custom render thẻ (renderItem) -> Mặc định phải là card
    if (renderItem) return 'card';
    // Ưu tiên 3: Nếu kích thước Widget đang bị thu hẹp lại (Mobile Mode) -> Ép về card để tránh vỡ bảng
    if (isMobile) return defaultViewMode=="compact"?defaultViewMode: 'card';
    // Ưu tiên 4: Lấy giá trị mặc định từ props (chỉ áp dụng khi Widget đủ rộng)
    if (defaultViewMode) return defaultViewMode;
    // Ưu tiên 5: Mặc định cuối cùng cho màn hình lớn
    return 'table';
  }, [manualViewMode, renderItem, isMobile, defaultViewMode]);

  // 3. Cập nhật lại hàm xử lý sự kiện
  const handleViewModeChange = useCallback((mode) => {
    setManualViewMode(mode);
  }, []);

  let dateField = fields.find(f => f.field === 'ngay_ct' && f.searchable !== false);
  if (!dateField) dateField = fields.find(f => (f.field === 'Date' || f.field === 'DateTime') && f.searchable !== false);

  const getUrlPrefill = () => {
    const params = new URLSearchParams(cleanSearch);
    let prefill = {};
    params.forEach((val, key) => {
      const fieldDef = fields.find(f => f.field === key); 
      if (fieldDef) prefill[key] = fieldDef.type === 'Number' ? Number(val) : fieldDef.type === 'Boolean' ? (val === 'true' || val === '1') : val;
    });
    return prefill;
  };

  useEffect(() => { 
    const timer = setTimeout(() => setDebouncedSearch(quickSearch), 500); 
    return () => clearTimeout(timer); 
  }, [quickSearch]);

  // Điều kiện chia HAI phần: phần KHÔNG thuộc bộ lọc nhanh (cấu hình, URL, ô tìm nhanh, lọc nâng cao,
  // form tìm kiếm) và điều kiện của TỪNG hàng lọc nhanh. Tách như vậy để đếm số bản ghi trên chip của
  // một hàng thì lấy được điều kiện của MỌI THỨ KHÁC trừ chính hàng đó (đúng counterQuery của bộ cũ).
  const nonQuickParts = useMemo(() => {
    const conditionArray = []; 
    if (condition && Object.keys(condition).length > 0) conditionArray.push({ ...condition });

    const urlParams = new URLSearchParams(cleanSearch);
    const urlConditions = {};
    for (const key of urlParams.keys()) {
      const fieldDef = fields.find(f => f.field === key && f.searchable !== false);
      if (fieldDef) {
        const allVals = urlParams.getAll(key);
        if (allVals.length > 1) urlConditions[key] = { $in: allVals.map(val => fieldDef.type === 'Number' ? Number(val) : fieldDef.type === 'Boolean' ? (val === 'true' || val === '1') : val) };
        else urlConditions[key] = fieldDef.type === 'Number' ? Number(allVals[0]) : fieldDef.type === 'Boolean' ? (allVals[0] === 'true' || allVals[0] === '1') : allVals[0];
      }
    }
    if (Object.keys(urlConditions).length > 0) conditionArray.push(urlConditions);

    if (debouncedSearch.trim() !== '') {
      const searchFields = fields.filter(f => (f.type === 'String' || f.type === 'string') && f.searchable !== false);
      if (searchFields.length > 0) conditionArray.push({ $or: searchFields.map(f => ({ [f.field]: { $regex: debouncedSearch.trim(), $options: 'i' } })) });
    }


    if (appliedFilters) {
      Object.keys(appliedFilters).forEach(key => {
        const val = appliedFilters[key];
        const fieldDef = fields.find(f => f.field === key);
        if (fieldDef && val !== undefined && val !== null && val !== '') {
          if (fieldDef.type === 'Date' || fieldDef.type === 'DateTime' || fieldDef.type === 'DateFilter') {
            const dateCond = buildDateCondition(val, key);
            if (dateCond) conditionArray.push(dateCond);
          } else if (fieldDef.type === 'Number' && typeof val === 'object') {
            const numCond = {};
            if (val.min !== '' && val.min !== undefined) numCond.$gte = Number(val.min);
            if (val.max !== '' && val.max !== undefined) numCond.$lte = Number(val.max);
            if (Object.keys(numCond).length > 0) conditionArray.push({ [key]: numCond });
          } else if (Array.isArray(val) && val.length > 0) conditionArray.push({ [key]: { $in: val } });
          else if ((fieldDef.type === 'String' || fieldDef.type === 'string') && typeof val === 'string') conditionArray.push({ [key]: { $regex: val.trim(), $options: 'i' } });
          else conditionArray.push({ [key]: val });
        }
      });
    }

    if (dateField && appliedDateFilter) {
      const mainDateCond = buildDateCondition(appliedDateFilter, dateField.field);
      if (mainDateCond) conditionArray.push(mainDateCond);
    }

    if (searchFormCondition && Object.keys(searchFormCondition).length) conditionArray.push(searchFormCondition);
    return conditionArray;
  }, [condition, cleanSearch, debouncedSearch, appliedFilters, appliedDateFilter, searchFormCondition, fields, dateField]);

  // Điều kiện của TỪNG hàng lọc nhanh (multiSel -> $in, lọc theo ngày -> khoảng ngày, prepareQuery…).
  const quickParts = useMemo(() => {
    const out = {};
    for (const qf of quickFilters) {
      const qc = quickFilterCondition(qf, activeQuickFilters[qf.field], (v) => quickFilterValue(v, context));
      if (qc) out[qf.field] = qc;
    }
    return out;
  }, [quickFilters, activeQuickFilters, context]);

  const finalCondition = useMemo(
    () => combineConditions([...nonQuickParts, ...Object.values(quickParts)]),
    [nonQuickParts, quickParts]
  );

  // Điều kiện ĐẾM cho chip của một hàng = mọi điều kiện khác, trừ chính hàng đó.
  const countBaseByField = useMemo(() => {
    const out = {};
    for (const qf of quickFilters) {
      if (!qf.showCount) continue;
      out[qf.field] = combineConditions([...nonQuickParts, ...Object.keys(quickParts).filter(k => k !== qf.field).map(k => quickParts[k])]);
    }
    return out;
  }, [quickFilters, nonQuickParts, quickParts]);


  // Người dùng ĐÃ nêu điều kiện gì chưa? Dùng cho `searchRequired`: màn hình nhiều năm dữ liệu thì
  // không tải gì cho tới khi có điều kiện. Điều kiện cứng sẵn có trong cấu hình/URL cũng được tính
  // (mở từ một màn hình khác kèm điều kiện thì không bắt gõ lại).
  const hasSearchInput = useMemo(() => (
    !!(appliedFilters && Object.keys(appliedFilters).length) ||
    !!appliedDateFilter ||
    !!(debouncedSearch && debouncedSearch.trim()) ||
    !!(searchFormCondition && Object.keys(searchFormCondition).length) ||
    quickFilters.some(q => {
      const v = activeQuickFilters[q.field];
      return v !== '' && v !== undefined && v !== null;
    }) ||
    !!(condition && Object.keys(condition).length)
  ), [appliedFilters, appliedDateFilter, debouncedSearch, searchFormCondition, activeQuickFilters, quickFilters, condition]);

  // ===== FORM TÌM KIẾM RIÊNG: nguồn field =====
  const inlineSearchFields = Array.isArray(searchFields) && searchFields.length ? searchFields : null;
  const { data: loadedSearchFields, isLoading: isLoadingSearchForm } = useQuery({
    // Cùng khoá cache với trang đầy đủ/widget (`napCauHinhDanhSach` trả cấu hình đã parse) — nạp một lần.
    queryKey: ['dataList_config', searchForm],
    queryFn: async () => {
      const { napCauHinhDanhSach } = await import('./DataDynamicPage');
      return napCauHinhDanhSach({ code: searchForm, type: 'list', context, userToken });
    },
    select: (cfg) => (cfg && Array.isArray(cfg.fields) ? cfg.fields : []),
    enabled: !!searchForm && !inlineSearchFields && !!userToken && authReady.canFetch && !headless,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const effectiveSearchFields = useMemo(
    () => prepareSearchFields(inlineSearchFields || loadedSearchFields || [], { dateTarget: dateField?.field || 'ngay_ct' }),
    [inlineSearchFields, loadedSearchFields, dateField]
  );
  const hasSearchForm = effectiveSearchFields.length > 0;
  useEffect(() => {
    if (searchForm && !inlineSearchFields && loadedSearchFields && loadedSearchFields.length === 0) {
      console.warn(`[DataListManager] "${code}": không tìm thấy form tìm kiếm "${searchForm}" (data_list_config/listinfo) -> dùng ngăn lọc thường.`);
    }
  }, [searchForm, inlineSearchFields, loadedSearchFields, code]);

  const handleApplySearchForm = useCallback((values) => {
    const rs = buildSearchCondition(values, effectiveSearchFields, {
      buildDateCondition, moment: Moment, numeral: utils.numeral, context,
    });
    if (rs.errors.length) context.alert('Điều kiện tìm kiếm lỗi: ' + rs.errors.join('; '), null, 'red');
    // Điều kiện trên đúng cột của một hàng lọc nhanh -> chuyển sang hàng đó (nút sáng đúng giá trị,
    // và hai nơi không cùng ép hai điều kiện ngược nhau lên một cột).
    const sync = syncQuickFiltersFromCondition(quickFilters, rs.condition, activeQuickFilters);
    if (sync.changed) setActiveQuickFilters(sync.values);
    setSearchFormValues(values);
    setSearchFormCondition(sync.rest);
  }, [effectiveSearchFields, context, quickFilters, activeQuickFilters]);

  const handleClearSearchForm = useCallback(() => {
    setSearchFormValues(null);
    setSearchFormCondition(null);
  }, []);

  // Nút Lọc: có form tìm kiếm riêng thì mở form đó, không thì mở ngăn lọc dựng từ các cột.
  const openFilterOrSearch = useCallback(() => {
    if (hasSearchForm) setSearchFormOpen(true); else setFilterOpen(true);
  }, [hasSearchForm]);

  // Lọc nâng cao áp điều kiện: đồng bộ sang hàng lọc nhanh cùng cột (như form tìm kiếm).
  const handleApplyFilter = useCallback((textFilters, dateFilter) => {
    const sync = syncQuickFiltersFromCondition(quickFilters, textFilters || {}, activeQuickFilters);
    if (sync.changed) setActiveQuickFilters(sync.values);
    setAppliedFilters(sync.changed ? sync.rest : textFilters);
    setAppliedDateFilter(dateFilter);
  }, [quickFilters, activeQuickFilters]);

  // Bật `searchRequired` mà chưa có điều kiện -> mở sẵn ngăn lọc MỘT LẦN cho người dùng biết phải nhập.
  // Có form tìm kiếm riêng thì mở form đó (đợi nạp xong field của nó đã).
  const searchPromptedRef = useRef(false);
  useEffect(() => {
    if (!searchRequired || hasSearchInput || headless || searchPromptedRef.current) return;
    if (searchForm && !inlineSearchFields && isLoadingSearchForm) return;
    searchPromptedRef.current = true;
    if (hasSearchForm) setSearchFormOpen(true); else setFilterOpen(true);
  }, [searchRequired, hasSearchInput, headless, searchForm, inlineSearchFields, isLoadingSearchForm, hasSearchForm]);

  // ================= REACT QUERY =================
  const { data: listData, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading: isLoadingList, isError: isErrorList, error, refetch: refetchList } = useInfiniteQuery({
    // `boundListinfoCode` nằm trong khoá: đổi listinfo mượn hành vi là đổi cả phân quyền lẫn dữ liệu
    // trả về, không được dùng lại cache của lần trước.
    queryKey: [context.config.id_app, code, 'list', context.appInfo?._id, { userToken, finalCondition, sort: currentSort.sort, limit: currentLimit, boundListinfoCode }],
    queryFn: async ({ pageParam = 1 }) => {
      // + gốc các trường điều kiện của nút dòng (showIfField, vd 'exfields') — không đọc thì nút không bao giờ hiện ở danh sách.
      const selectedFields = [...new Set([...fields.map(f => f.field), ...truongPhaiDoc(rowActions)])].join(',');
      const response = await context.apis.asyncGetList(userToken, finalApiCode, { condition: finalCondition, headers: apiHeaders, sort: currentSort.sort, page: pageParam, limit: currentLimit, fields: selectedFields, pagination: 1 });
      return response;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage || !lastPage.pagination) return undefined;
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined; 
    },
    staleTime: 2 * 60 * 1000, 
    gcTime: 5 * 60 * 1000,      
    // Đợi đủ điều kiện mới gọi: có user thật + ĐÃ CHỌN CÔNG TY + không đang xác thực dở.
    // Bản cũ chỉ kiểm `!isPublicUser` nên vừa đăng nhập xong (chưa chọn công ty) là bắn request
    // với id_app rỗng.
    // `searchRequired`: bảng nhiều năm dữ liệu thì KHÔNG tải gì cho tới khi người dùng nhập điều kiện
    // (tương đương search_required của listinfo). Có sẵn điều kiện cứng từ cấu hình/URL thì tải luôn.
    enabled: !!userToken && authReady.canFetch && !headless && !!finalApiCode && (!searchRequired || hasSearchInput),
  });

  const allItems = useMemo(() => {
    if (!listData) return [];
    const rawData = listData.pages.flatMap(page => page?.data || []);
    return typeof transformData === 'function' ? transformData(rawData) : rawData;
  }, [listData, transformData]);

  const totalRecords = listData?.pages[0]?.pagination?.total || allItems.length;

  // =====================================================================
  // SỬA THẲNG TRÊN LƯỚI DANH SÁCH (bù col_editable của InputBase, nhưng theo TỪNG CỘT)
  // ---------------------------------------------------------------------
  // Bản nháp gom THEO DÒNG: sửa bao nhiêu ô tuỳ thích, rời dòng (hoặc Enter / nút Lưu) thì gửi MỘT lần.
  // Xem inlineEdit.js cho toàn bộ luật thuần.
  // =====================================================================
  const [drafts, setDrafts] = useState({});
  const [savingRowId, setSavingRowId] = useState(null);

  const inlineEditOn = canInlineEdit({ canEdit, viewMode, isMobile });

  // Dữ liệu ĐỂ HIỂN THỊ = dữ liệu server phủ bản nháp lên trên. Không có nháp nào thì trả về ĐÚNG mảng
  // gốc (giữ nguyên tham chiếu — điều kiện để memo của DataListItems/dòng còn tác dụng).
  const displayItems = useMemo(() => {
    if (!inlineEditOn || Object.keys(drafts).length === 0) return allItems;
    return allItems.map(it => draftedRow(it, drafts, (it._id || it.id)));
  }, [allItems, drafts, inlineEditOn]);

  const dirtyIds = useMemo(
    () => (inlineEditOn ? computeDirtyRowIds(allItems, drafts, deepEqual) : []),
    [allItems, drafts, inlineEditOn]
  );

  // Cột này, ở DÒNG này, có sửa được không. Dùng ref cho các giá trị đổi theo render để hàm ỔN ĐỊNH.
  const inlineCtxRef = useRef(null);
  inlineCtxRef.current = { context, fields, masterData, on: inlineEditOn };
  const isEditableCell = useCallback((field, row) => {
    const c = inlineCtxRef.current;
    if (!c.on) return false;
    return resolveEditableInList(field, {
      context: c.context, formData: row, masterData: c.masterData || row, fields: c.fields,
    });
  }, []);

  const handleCellChange = useCallback((row, fieldName, value) => {
    const id = row && (row._id || row.id);
    if (id === undefined || id === null) return;
    // Cùng lý do như DataListForm.handleFieldChange: ô số trả về CHUỖI, để nguyên là PUT chuỗi lên server.
    const cfg = ((inlineCtxRef.current || {}).fields || []).find(f => f.field === fieldName);
    setDrafts(prev => setDraftValue(prev, id, fieldName, coerceFieldValue(cfg, value)));
  }, []);

  const handleCancelRow = useCallback((row) => {
    const id = row && (row._id || row.id);
    setDrafts(prev => clearDraft(prev, id));
  }, []);

  // Giữ trong ref để `handleCommitRow` ổn định: nó được truyền xuống tận từng dòng của bảng.
  const commitCtxRef = useRef(null);
  const handleCommitRow = useCallback(async (row) => {
    const c = commitCtxRef.current;
    const id = row && (row._id || row.id);
    if (id === undefined || id === null) return;
    // `row` truyền vào là dòng ĐANG HIỂN THỊ (đã phủ nháp). Bản gốc để so là dòng của server.
    const original = c.allItems.find(it => (it._id || it.id) === id);
    const changed = original ? pendingChanges(original, c.drafts, id, deepEqual) : null;
    if (!changed) { setDrafts(prev => clearDraft(prev, id)); return; }  // bấm vào rồi bấm ra: không gửi gì

    const merged = { ...original, ...changed };
    // Cùng một cửa kiểm tra với form: chứng từ sửa trên lưới cũng phải qua checkBeforeSave.
    if (typeof c.checkBeforeSave === 'function') {
      const rs = await c.runHook('checkBeforeSave', { master: c.masterData || merged, detail: merged });
      if (rs.blocked) {
        c.context.alert(rs.message || "Dữ liệu chưa hợp lệ", null, "red");
        return;   // GIỮ bản nháp để người dùng sửa tiếp, không âm thầm vứt đi
      }
    }

    setSavingRowId(id);
    try {
      const payload = { ...buildSavePayload(merged, original, c.fields), id_app: c.context.config?.id_app || "" };
      const response = await c.context.apis.asyncPostList(c.userToken, c.finalApiCode, payload, { headers: c.apiHeaders });
      if (response && response.error) throw new Error(response.error);
      setDrafts(prev => clearDraft(prev, id));
      c.queryClient.invalidateQueries({ queryKey: [c.context.config.id_app, c.code, 'list'] });
      c.runHook('onSaved', { master: c.masterData || response, detail: response || merged, origin_data: payload, action: 'EDIT' })
        .then(({ message }) => { if (message) c.context.alert(message, null, "red"); });
    } catch (e) {
      // GIỮ bản nháp: xoá đi là người dùng mất công gõ mà không hiểu vì sao.
      c.context.alert(e.message || "Lưu thất bại!", null, "red");
    } finally {
      setSavingRowId(null);
    }
  }, []);

  // Chia đôi màn hình: mặc định BẬT khi cấu hình có lưới chi tiết, đang ở chế độ bảng và màn đủ rộng.
  const isSplitView = useMemo(
    () => shouldShowDetailPane({ showDetailOnGrid, fields, viewMode, isMobile }),
    [showDetailOnGrid, fields, viewMode, isMobile]
  );
  const activeRow = useMemo(
    () => (isSplitView && activeRowId ? allItems.find(it => (it._id || it.id) === activeRowId) || null : null),
    [isSplitView, activeRowId, allItems]
  );
  const selectRow = useCallback((item) => {
    setActiveRowId(prev => {
      const id = item && (item._id || item.id);
      return prev === id ? prev : id;   // bấm lại đúng dòng đó thì giữ nguyên, khỏi vẽ lại
    });
  }, []);
  // =====================================================================
  // BỘ CHẠY SCRIPT VÒNG ĐỜI
  // ---------------------------------------------------------------------
  // Giữ script trong ref để `runHook` KHÔNG BAO GIỜ đổi identity: nó được truyền xuống lưới (onCellClick)
  // và nằm trong dependency của các mutation; đổi identity là memo của dòng vô hiệu -> vẽ lại toàn lưới.
  // =====================================================================
  const hookScriptsRef = useRef(null);
  hookScriptsRef.current = { onInit, onInitUpdate, onSaved, onDeleting, onDeleted, handleCellClicked };
  const hookCtxRef = useRef(null);
  hookCtxRef.current = { context, history, fields, masterData, code, isMobile };

  // Tham số cho script `default` (xem fieldDefaults.js). Đi qua ref cùng lối với hookCtxRef: hàm lấy
  // tham số giữ nguyên identity nên useEffect mở form headless không phải khai chúng vào dependency —
  // khai vào là effect chạy lại mỗi lần đổi masterData và mở lại form.
  const defaultArgsRef = useRef(null);
  defaultArgsRef.current = { master: masterData, context, token: userToken, mediaQueryMatches: isMobile };
  const defaultScriptArgs = useCallback(() => defaultArgsRef.current, []);

  /**
   * Chạy một script vòng đời. Luôn trả `{ blocked, message, result }` — KHÔNG bao giờ ném ra ngoài.
   * Script lỗi ở hook CHẶN ĐƯỢC thì coi là CHẶN: script kiểm tra mà hỏng thì phải dừng, không được
   * lặng lẽ cho qua (đây đúng là chỗ InputBase để lọt với on_deleting — nó bắt lỗi rồi vẫn xoá tiếp).
   */
  const runHook = useCallback(async (hookName, bag) => {
    const fn = hookScriptsRef.current[hookName];
    if (typeof fn !== 'function') return { blocked: false, message: null, result: undefined };
    const c = hookCtxRef.current;
    const full = {
      API: c.context.apis, context: c.context, utils, history: c.history,
      fields: c.fields, module: c.code, mediaQueryMatches: c.isMobile,
      masterData: c.masterData,
      ...bag,
    };
    try {
      const rs = await fn(...buildHookArgs(hookName, full));
      return { ...interpretHookResult(hookName, rs), result: rs };
    } catch (e) {
      console.error(`[DataListManager] script ${hookName} lỗi:`, e);
      const msg = e?.message || e?.error || String(e);
      const blocking = interpretHookResult(hookName, { error: 'x' }).blocked;
      return { blocked: blocking, message: msg, result: undefined };
    }
  }, []);

  // Túi giá trị cho `handleCommitRow` (khai ở trên, trước `runHook`). Gán ở ĐÂY vì phải có `runHook`.
  // Nhờ đi qua ref mà `handleCommitRow` giữ nguyên identity — nó được truyền xuống TỪNG DÒNG của bảng.
  commitCtxRef.current = {
    allItems, drafts, fields, masterData, checkBeforeSave, code, finalApiCode,
    userToken, context, queryClient, runHook, apiHeaders,
  };

  // Bấm vào một Ô của bảng: script handle_cell_clicked chạy TRƯỚC và CHẶN được việc chọn dòng
  // (trả `false` là dừng im lặng — vd script đã tự điều hướng sang màn khác).
  // `column` là object field của cột được bấm, null khi bấm vào ô dính (checkbox / STT / thao tác).
  const handleRowClick = useCallback(async (item, columnField) => {
    const column = columnField ? (hookCtxRef.current.fields || []).find(f => f.field === columnField) || null : null;
    const rs = await runHook('handleCellClicked', { row: item, column });
    if (rs.message) hookCtxRef.current.context.alert(rs.message, null, "red");
    if (rs.blocked) return;
    selectRow(item);
  }, [runHook, selectRow]);


  // Tải xong danh sách thì CHỌN SẴN DÒNG ĐẦU (nếu có) để khung chi tiết không trống trơn.
  // Dòng đang chọn còn trong danh sách thì giữ nguyên — cuộn tải thêm không được nhảy về đầu.
  useEffect(() => {
    if (!isSplitView) return;
    const next = resolveActiveRowId(allItems, activeRowId);
    if (next !== activeRowId) setActiveRowId(next);
  }, [isSplitView, allItems, activeRowId]);

  const { data: viewItem, isLoading: isLoadingView } = useQuery({
    queryKey: [context.config.id_app, code, 'detail', viewId, { userToken }],
    queryFn: async () => {
      const cachedItem = allItems.find(item => (item._id || item.id) === viewId);
      if (cachedItem) return cachedItem; 
      let fetchedData = await context.apis.asyncGetDataById(userToken, finalApiCode, viewId);
      if (fetchedData && typeof transformData === 'function') fetchedData = transformData([fetchedData])[0];
      return fetchedData;
    },
    enabled: !!viewId && !!userToken && authReady.canFetch && !!finalApiCode,
    staleTime: 5 * 60 * 1000,
  });

  const saveMutation = useMutation({
    mutationFn: async (finalFormData) => {
      // Chỉ gửi phần THẬT SỰ đổi khi cập nhật (xem formRules.buildSavePayload): tránh ghi đè kết quả
      // duyệt/sửa của người khác bằng dữ liệu đọc lúc mở form, và không bao giờ đẩy field tạm lên server.
      const payload = { ...buildSavePayload(finalFormData, currentEditItem, fields), id_app: context.config?.id_app || "" };
      const response = await context.apis.asyncPostList(userToken, finalApiCode, payload, { headers: apiHeaders });
      if (response && response.error) throw new Error(response.error);
      return typeof transformData === 'function' ? transformData([response])[0] : response;
    },
    onSuccess: (savedItem, sentFormData) => {
      queryClient.invalidateQueries({ queryKey: [context.config.id_app, code, 'list'] });
      // SAU KHI LƯU — on_saved. Đã lưu xong rồi nên không chặn được gì; script hỏng chỉ báo cho người
      // dùng chứ tuyệt đối không được làm hỏng luồng đóng form / chuyển trang bên dưới.
      runHook('onSaved', {
        master: masterData || savedItem, detail: savedItem,
        origin_data: sentFormData, action: isEditing ? 'EDIT' : 'ADD',
      }).then(({ message }) => { if (message) context.alert(message, null, "red"); });
      if (headless) {
          if (headlessConfig?.onSuccess) headlessConfig.onSuccess(savedItem);
          onHeadlessClose();
          context.alert(isEditing ? "Cập nhật thành công!" : "Thêm mới thành công!", null, "green");
          return; 
      }
      handleCloseForm();
      context.alert(isEditing ? "Cập nhật thành công!" : "Thêm mới thành công!", null, "green");
      if (typeof onSaveSuccess === 'function') onSaveSuccess(savedItem, isEditing);
      if (!isEditing && dynamicRedirect) {
        let targetUrl = typeof dynamicRedirect === 'function' ? dynamicRedirect(savedItem) : dynamicRedirect.includes('{id}') ? dynamicRedirect.replace('{id}', savedItem._id || savedItem.id) : `${dynamicRedirect}/${savedItem._id || savedItem.id}`;
        setTimeout(() => { history.push(targetUrl); setDynamicRedirect(null); }, 500);
      }
    },
    onError: (error) => context.alert(error.message || "Quá trình lưu thất bại!", null, "red")
  });

  const deleteMutation = useMutation({
    // Nhận CẢ bản ghi (không chỉ id) để script on_deleting/on_deleted có dữ liệu mà xét.
    mutationFn: async (target) => {
      const id = typeof target === 'string' ? target : (target?._id || target?.id);
      const record = typeof target === 'string' ? (allItems.find(it => (it._id || it.id) === target) || { _id: target }) : target;
      // TRƯỚC KHI XOÁ — on_deleting. Trả {error}/chuỗi thì DỪNG, không gọi API xoá.
      const before = await runHook('onDeleting', { master: masterData || record, detail: record });
      if (before.blocked) throw new Error(before.message || "Không thể xoá bản ghi này");
      const rs = await context.apis.asyncDeleteList(userToken, finalApiCode, id);
      if (rs && rs.error) throw new Error(rs.error);
      return record;
    },
    onSuccess: (deletedRecord) => {
      queryClient.invalidateQueries({ queryKey: [context.config.id_app, code, 'list'] });
      context.alert("Xóa dữ liệu thành công!", null, "green");
      if (viewId) handleCloseView();
      // SAU KHI XOÁ — on_deleted. `rows` là danh sách CÒN LẠI đang hiển thị.
      const remain = allItems.filter(it => (it._id || it.id) !== (deletedRecord?._id || deletedRecord?.id));
      runHook('onDeleted', { master: masterData || deletedRecord, rows: remain, detail: deletedRecord })
        .then(({ message }) => { if (message) context.alert(message, null, "red"); });
    },
    onError: (error) => context.alert(error.message || "Xóa thất bại!", null, "red")
  });

  const deleteManyMutation = useMutation({
    mutationFn: async (idsArray) => {
        const records = idsArray.map(id => allItems.find(it => (it._id || it.id) === id) || { _id: id });
        // on_deleting chạy cho TỪNG bản ghi và chặn được CẢ LỆNH: một bản ghi không được phép xoá thì
        // dừng trước khi gọi API, chứ không xoá dở nửa chừng rồi mới báo lỗi.
        for (const r of records) {
          const before = await runHook('onDeleting', { master: masterData || r, detail: r });
          if (before.blocked) throw new Error(before.message || "Không thể xoá bản ghi này");
        }
        const rs = await Promise.all(idsArray.map(id => context.apis.asyncDeleteList(userToken, finalApiCode, id)));
        const failed = rs.find(r => r && r.error);
        if (failed) throw new Error(failed.error);
        const remain = allItems.filter(it => idsArray.indexOf(it._id || it.id) < 0);
        for (const r of records) {
          const after = await runHook('onDeleted', { master: masterData || r, rows: remain, detail: r });
          if (after.message) context.alert(after.message, null, "red");
        }
        return rs;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [context.config.id_app, code, 'list'] });
      context.alert("Xóa hàng loạt thành công!", null, "green");
      setSelectedIds([]); 
      setIsSelectionMode(false);
    },
    onError: (error) => context.alert(error.message || "Xóa hàng loạt thất bại!", null, "red")
  });

  const handlePrintClick = () => {
      if (viewMode === 'card' && !isSelectionMode) {
          setIsSelectionMode(true);
          context.alert("Đã bật chế độ chọn. Vui lòng tick chọn các thẻ, sau đó bấm nút In lần nữa.", null, "info");
      } else {
          setShowPrintDialog(true);
      }
  };

  const handleDeleteManyClick = () => {
      if (viewMode === 'card' && !isSelectionMode) {
          setIsSelectionMode(true);
          context.alert("Đã bật chế độ chọn. Vui lòng tick chọn các thẻ, sau đó bấm nút Xóa lần nữa.", null, "warning");
      } else {
          context.alert(`Bạn có chắc chắn muốn xóa vĩnh viễn ${selectedIds.length} bản ghi đã chọn?`, (yes) => {
            if(yes) deleteManyMutation.mutate(selectedIds);
          });
      }
  };

  useImperativeHandle(ref, () => ({
    openAddForm: (prefillData = {}) => handleOpenForm(null, prefillData),
    refresh: () => refetchList(),
    updateLocalItem: () => queryClient.invalidateQueries({ queryKey: [context.config.id_app, code, 'list'] }),
  }));

  // =====================================================================
  // EFFECT LẮNG NGHE URL - CHỈ KÍCH HOẠT NẾU LÀ MAIN LIST
  // =====================================================================
  useEffect(() => {
    // Nếu component không phải là chủ của URL này, bỏ qua hoàn toàn tham số
    if (headless || !isMainUrlList) return; 

    if (skipUrlEffectRef.current) {
        skipUrlEffectRef.current = false;
        return;
    }

    const queryParams = new URLSearchParams(cleanSearch);
    const action = queryParams.get('action');
    const id = queryParams.get('id');

    if (action === 'add') {
      if (!canAdd) { context.alert("Bạn không có quyền!", null, "red"); return; }
      const redirectUrl = queryParams.get('redirect');
      if (redirectUrl) setDynamicRedirect(redirectUrl);
      
      if (!currentEditItem || !formOpen) {
          handleOpenForm(null, getUrlPrefill(), true);
      }
    } else if (action === 'view' && id) { 
      setViewId(id); 
    } else if (action === 'edit' && id) {
      const itemToEdit = allItems.find(item => (item._id || item.id) === id);
      if (itemToEdit) handleOpenForm(itemToEdit, {}, true);
    } else if (!action) { 
      setFormOpen(false); setViewId(null); 
    }
  }, [cleanSearch, canAdd, allItems, headless, isMainUrlList]);

  useEffect(() => {
    if (headless && headlessConfig) {
       if (headlessConfig.action === 'add') {
           setIsEditing(false);
           const currentFields = headlessConfig.fields || fields || [];
           // `default` được phép là SCRIPT BẤT ĐỒNG BỘ (xin số chứng từ, tỷ giá hôm nay…) nên phải CHỜ
           // rồi mới mở form — mở trước thì người dùng thấy form rỗng rồi giá trị nhảy vào sau.
           // Cờ `cancelled` chặn ghi kết quả CŨ đè lên khi headlessConfig đổi giữa chừng.
           let cancelled = false;
           (async () => {
             const fieldDefaults = await resolveFieldDefaults(currentFields, defaultScriptArgs(), DEFAULT_SCRIPT_DEPS);
             if (cancelled) return;
             setCurrentEditItem({ ...fieldDefaults, ...(optionDefaults || {}), ...defaultValues, ...(headlessConfig.prefillData || {}) });
             setFormOpen(true); setViewId(null);
           })();
           return () => { cancelled = true; };
       } else if (headlessConfig.action === 'edit') {
           setIsEditing(true); setCurrentEditItem(headlessConfig.itemData);
           setFormOpen(true); setViewId(null);
       } else if (headlessConfig.action === 'view') {
           setFormOpen(false); setViewId(headlessConfig.id);
       }
    }
  }, [headless, headlessConfig]);

  // =====================================================================
  // HÀM XỬ LÝ MỞ FORM (CHỈ CẬP NHẬT URL NẾU LÀ MAIN LIST)
  // =====================================================================
  const handleOpenForm = async (itemToEdit, prefill = {}, fromUrlEffect = false) => {
    if (itemToEdit && onEdit) return onEdit(itemToEdit);
    if (!itemToEdit && onAdd) return onAdd(prefill);
    
    setIsEditing(!!itemToEdit);
    // Chỉ tính khi TẠO MỚI: script `default` được phép gọi API (xin số chứng từ tiếp theo), chạy nó mỗi
    // lần mở form SỬA là vừa tốn một lượt gọi vừa có thể cấp phát nhầm số.
    const fieldDefaults = itemToEdit ? {} : await resolveFieldDefaults(fields, defaultScriptArgs(), DEFAULT_SCRIPT_DEPS);

    // Khi TẠO MỚI: tự điền giá trị tuỳ chọn mặc định (options.option) — sau field.default,
    // trước defaultValues/prefill (URL) để các giá trị tường minh vẫn ghi đè được.
    const data = itemToEdit || { ...fieldDefaults, ...(optionDefaults || {}), ...defaultValues, ...prefill };

    // SCRIPT KHỞI TẠO FORM — on_init (thêm mới) / on_init_update (sửa).
    // Script sửa THẲNG vào `data` để đặt giá trị mặc định (đúng phương ngữ InputBase: nó nhận `detail`
    // rồi gán vào chính object đó), nên phải chạy TRƯỚC setCurrentEditItem.
    // Trả {error} — hoặc `false` với on_init — thì KHÔNG mở form.
    const initHook = itemToEdit ? 'onInitUpdate' : 'onInit';
    const init = await runHook(initHook, {
      master: masterData || data, detail: data, action: itemToEdit ? 'EDIT' : 'ADD',
    });
    if (init.blocked) {
      if (init.message) context.alert(init.message, null, "red");
      return;
    }

    setCurrentEditItem(data);
    setFormOpen(true);

    // Chỉ push vào lịch sử URL nếu đây là component gốc của trang
    if (!fromUrlEffect && isMainUrlList) {
        skipUrlEffectRef.current = true;
        const params = new URLSearchParams(location.search);
        params.set('action', itemToEdit ? 'edit' : 'add');
        if (itemToEdit) params.set('id', itemToEdit._id || itemToEdit.id);
        
        history.push(`${location.pathname}?${params.toString()}`);
    }
  };

  const handleCopy = useCallback((item) => {
    if (!canAdd) {
      context.alert("Bạn không có quyền thêm mới, do đó không thể copy!", null, "red");
      return;
    }

    const clonedItem = JSON.parse(JSON.stringify(item));
    delete clonedItem._id;
    delete clonedItem.id;
    delete clonedItem.date_created;
    delete clonedItem.date_updated;
    delete clonedItem.user_created;
    delete clonedItem.user_updated;
    delete clonedItem.__v; 
    // Bỏ mọi thông tin thuộc về BẢN GỐC (saoChepBanGhi.js): nguồn đơn hàng, chiến dịch / quảng cáo, utm, mã click, phiên chat
    // (dấu "đơn chốt từ hội thoại" của Hộp thư), mã đơn sàn, định danh lead… — mang theo thì sai báo cáo marketing và thông tin
    // đơn của khách này có thể bị gửi vào hội thoại của khách kia. Máy chủ tự xác định lại nguồn cho bản mới.
    boThongTinKhiSaoChep(clonedItem, apiCode || code);

    handleOpenForm(null, clonedItem);
  }, [canAdd, context, isMainUrlList, apiCode, code]);

  const handleOpenView = (item) => {
    setViewId(item._id || item.id);
    
    if (isMainUrlList) {
        skipUrlEffectRef.current = true;
        const params = new URLSearchParams(location.search);
        params.set('action', 'view'); 
        params.set('id', item._id || item.id);
        history.push(`${location.pathname}?${params.toString()}`);
    }
  };

  // =====================================================================
  // HÀM XỬ LÝ ĐÓNG FORM/VIEW
  // =====================================================================
  const handleCloseForm = () => { 
    if (headless) {
      if (headlessConfig?.onCancel) headlessConfig?.onCancel();
      onHeadlessClose(); 
      return; 
    }
    setFormOpen(false); 
    
    if (isMainUrlList) {
        skipUrlEffectRef.current = true;
        const params = new URLSearchParams(location.search); 
        params.delete('action'); params.delete('id'); 
        
        if (history.action === 'PUSH') history.goBack();
        else history.replace(`${location.pathname}?${params.toString()}`);
    }
  };
  
  const handleCloseView = () => { 
    if (headless) { onHeadlessClose(); return; }
    setViewId(null); 
    
    if (isMainUrlList) {
        skipUrlEffectRef.current = true;
        const params = new URLSearchParams(location.search); 
        params.delete('action'); params.delete('id'); 
        
        if (history.action === 'PUSH') history.goBack();
        else history.replace(`${location.pathname}?${params.toString()}`);
    }
  };

  const handleDelete = (id) => context.alert("Bạn có chắc chắn muốn xóa bản ghi này?", (yes) => { if(yes) deleteMutation.mutate(id); });
  
  const handleScroll = (event) => {
    saveScroll(event);
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 50 && hasNextPage && !isFetchingNextPage) fetchNextPage();
  };

  // THAM SỐ cho nút chức năng riêng — dựng NGAY LÚC BẤM (điều kiện lọc và dòng đang chọn có thể đã
  // đổi từ lần vẽ trước). `goiRoute` gọi route của chính danh sách này: `/<apiCode>/<tên route>`.
  const layThamSoNut = () => ({
    context,
    goiRoute: taoGoiRoute({
      apis: context.apis, apiCode: finalApiCode, token: userToken,
      idApp: context.config?.id_app || (typeof context.config?.get === 'function' ? context.config.get('id_app') : '') || '',
    }),
    condition: finalCondition,
    selectedIds,
    selectedItems: allItems.filter((x) => selectedIds.includes(x._id)),
    refresh: () => refetchList(),
    quickAction, history, code,
  });
  // NÚT DÒNG — tham số dựng lúc bấm; `refresh` nạp lại cả danh sách lẫn bản ghi đang xem (nút thường đổi chính bản ghi đó,
  // vd tạo cơ hội -> lead sang "Cơ hội"). useCallback: các dòng là memo, hàm đổi mỗi lần vẽ thì cả bảng vẽ lại.
  const coNutDong = Array.isArray(rowActions) && rowActions.length > 0;
  const veNutDong = useCallback((item, noi) => {
    if (!coNutDong || !item) return null;
    const idApp = context.config?.id_app || (typeof context.config?.get === 'function' ? context.config.get('id_app') : '') || '';
    const lamMoi = () => {
      refetchList();
      if (item && item._id) queryClient.invalidateQueries({ queryKey: [context.config.id_app, code, 'detail', item._id] });
    };
    const layThamSo = (dong) => ({
      context, goiRoute: taoGoiRoute({ apis: context.apis, apiCode: finalApiCode, token: userToken, idApp }),
      item: dong, refresh: lamMoi, quickAction, history, code, apis: context.apis, token: userToken,
    });
    return <DataRowActions actions={rowActions} item={item} noi={noi} layThamSo={layThamSo} context={context} onXong={lamMoi} />;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coNutDong, rowActions, context, finalApiCode, userToken, quickAction, history, code]);

  const nutRieng = (Array.isArray(listActions) && listActions.length)
    ? <DataListActions actions={listActions} layThamSo={layThamSoNut} context={context} onXong={() => refetchList()} />
    : null;

  // =====================================================================
  // NÚT "CẤU HÌNH MÀN HÌNH" — mở form của trình khai báo (DataListConfigManager) ngay tại chỗ:
  //   - màn hình ĐÃ có data_list_config  -> SỬA bản ghi đó;
  //   - CHƯA có (đang chạy từ listinfo hoặc cấu hình truyền thẳng) -> THÊM MỚI, điền sẵn bản dịch THÔ
  //     của cấu hình hiện tại (script còn là chuỗi) để khỏi khai lại từ đầu.
  // Lưu xong thì xoá cache cấu hình -> trang/widget nạp lại cấu hình mới ngay.
  // =====================================================================
  // ===== NÚT "HƯỚNG DẪN": chỉ hiện khi màn hình khai file hướng dẫn và dựng được URL hợp lệ =====
  const [guideOpen, setGuideOpen] = useState(false);
  const guideUrl = useMemo(() => duongDanHuongDan(guideFile, context.config?.server_url), [guideFile, context.config?.server_url]);

  const isConfigAdmin = !!context.userInfo?.admin;
  const showConfigButton = !headless && code && code !== 'data_list_config'
    && (canConfigure === true || (canConfigure !== false && isConfigAdmin));
  const [configEditor, setConfigEditor] = useState(null);
  const closeConfigEditor = useCallback(() => setConfigEditor(null), []);
  const handleConfigSaved = useCallback(() => {
    setConfigEditor(null);
    queryClient.invalidateQueries({ queryKey: ['dataList_config', code] });
    context.alert("Đã lưu cấu hình màn hình. Màn hình sẽ nạp lại theo cấu hình mới.", null, "green");
  }, [queryClient, code, context]);
  const handleOpenConfig = useCallback(async () => {
    try {
      const { napCauHinhDanhSach } = await import('./DataDynamicPage');
      const { taoBanNhapCauHinh } = await import('./dataListConfigDraft');
      const raw = await napCauHinhDanhSach({ code, type: 'list', context, userToken, tho: true });
      if (raw && raw.source === 'data_list_config') { setConfigEditor({ existing: raw.item }); return; }
      const prefill = taoBanNhapCauHinh(raw && raw.item, { moduleCode: code, apiCode: finalApiCode, title, fields, listinfoCode });
      setConfigEditor({ prefill });
    } catch (e) {
      context.alert(e?.message || "Không mở được cấu hình màn hình", null, "red");
    }
  }, [code, context, userToken, finalApiCode, title, fields, listinfoCode]);

  const renderToolbar = (isHeader) => (
    <DataListActionBar
      isHeaderMode={isHeader} quickSearch={quickSearch} onQuickSearchChange={setQuickSearch} isLoading={isLoadingList}
      onOpenFilter={openFilterOrSearch} canAdd={canAdd} onAdd={() => handleOpenForm(null, getUrlPrefill())}
      hideHeader={hideHeader} sortOptions={sortOptions} currentSort={currentSort} onSortChange={setCurrentSort} actionBarPosition={actionBarPosition}
    />
  );

  // Gọi lại hook kèm `error` để tính cả trường hợp 401 (token hỏng hẳn sau khi APIs đã thử làm mới).
  const authGate = useAuthGate(requireLogin, { error });

  const { scrollRef, saveScroll } = useScrollRestoration({ key: code, isReady: allItems.length > 0, condition: !viewId });

  // Chắn màn hình theo PHA XÁC THỰC: đang đăng nhập/chọn công ty -> vòng quay; thiếu công ty ->
  // mời chọn công ty; thật sự chưa đăng nhập -> mời đăng nhập.
  if(authGate.blocked){
    return (
      <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column', bgcolor: IOS_BG, ...containerSx }}>
        {!hideHeader && <DataListHeader title={title} onBack={onBack}/>}
        <Box sx={{flex:1}}><LockScreen {...authGate.lockProps}/></Box>
      </Box>
    )
  }

  if (viewId && viewItem) {
    return (
      <Box sx={{ height: '100dvh', width: '100%', bgcolor: IOS_BG, ...containerSx }}>
        {renderView 
          ? renderView(viewItem, handleCloseView, () => handleOpenForm(viewItem), () => handleDelete(viewId), () => handleCopy(viewItem))
          : <DataView 
              item={viewItem} fields={fields} context={context} loading={isLoadingView} 
              canEdit={canEdit} canDelete={canDelete} 
              canCopy={canAdd && canCopy} 
              onBack={handleCloseView} onEdit={(item) => handleOpenForm(item)} 
              onDelete={handleDelete} 
              onCopy={handleCopy}
              customViewScript={customViewScript}
              apiCode={finalApiCode}
              changeCodeForm={changeCodeForm}
              viewTabs={viewTabs}
              moduleCode={code}
              renderRowActions={coNutDong ? veNutDong : null}
              readOnlyFiles={!canEdit}
              onRecordChanged={() => { refetchList(); queryClient.invalidateQueries({ queryKey: [context.config.id_app, code, 'detail', viewId] }); }}
            />}
      </Box>
    );
  }

  if (headless) {
      if (formOpen) return <DataListForm open={formOpen} customFormScript={customFormScript} onClose={handleCloseForm} isEditing={isEditing} initialData={currentEditItem} fields={fields} saving={saveMutation.isPending} onSave={onSave || ((formData) => saveMutation.mutate(formData))} formSize={formSize} checkBeforeSave={checkBeforeSave} apiCode={finalApiCode} changeCodeForm={changeCodeForm} autoGroupMaster={autoGroupMaster} />
      return null;
  }

  return (
    <React.Fragment>
      <Box ref={containerRef} sx={{ height: '100dvh', display: 'flex', flexDirection: 'column', bgcolor: IOS_BG, ...containerSx }}>
        <Box sx={{ flexShrink: 0 }}>
          {!hideHeader && <DataListHeader
              title={title} onBack={onBack} sortOptions={sortOptions} currentSort={currentSort} onSortChange={setCurrentSort}
              headerRight={nutRieng ? <React.Fragment>{nutRieng}{headerRight}</React.Fragment> : headerRight}
              viewMode={viewMode} onViewModeChange={!renderItem ? handleViewModeChange : null}

              onOpenOptions={quickAction?.openEdit ? handleOpenOptions : null}
              onOpenConfig={showConfigButton ? handleOpenConfig : null}
              onOpenGuide={guideUrl ? () => setGuideOpen(true) : null}

              
              onPrint={handlePrintClick}
              disablePrint={false}
              
              onDeleteMany={canDelete ? handleDeleteManyClick : null} 
              disableDeleteMany={(viewMode === 'table' || isSelectionMode) ? selectedIds.length === 0 || deleteManyMutation.isPending : false}
              actionContent={isSmUp ? renderToolbar(true) : null}
          />}
          {actionBarPosition === 'top' && !isSmUp && renderToolbar(false)}
          {quickFilters.map(qf => (
            <DataListQuickFilter
              key={qf.field}
              config={qf}
              activeValue={activeQuickFilters[qf.field] ?? ''}
              onChange={(val) => setActiveQuickFilters(prev => ({ ...prev, [qf.field]: val }))}
              countApiCode={qf.showCount ? finalApiCode : undefined}
              countHeaders={qf.showCount ? apiHeaders : undefined}
              countBaseCondition={countBaseByField[qf.field]}
            />
          ))}
        </Box>

        {(appliedFilters || appliedDateFilter || searchFormCondition) && (
          <Box sx={{ px: 2, mb: 1, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="primary" sx={{ fontStyle: 'italic', fontWeight: 'bold' }}>*Đang lọc dữ liệu</Typography>
            {searchFormCondition && (
              <Button size="small" onClick={() => setSearchFormOpen(true)} sx={{ textTransform: 'none', py: 0, minWidth: 0 }}>Sửa điều kiện</Button>
            )}
            {searchFormCondition && !searchRequired && (
              <Button size="small" color="inherit" onClick={handleClearSearchForm} sx={{ textTransform: 'none', py: 0, minWidth: 0, opacity: 0.7 }}>Bỏ lọc</Button>
            )}
          </Box>
        )}
        
        <Box ref={scrollRef} onScroll={handleScroll} sx={{ 
            flexGrow: 1, 
            overflowY: viewMode === 'table' ? 'hidden' : 'auto', 
            display: 'flex', flexDirection: 'column', px: viewMode === 'table'?0:2, 
            pb: actionBarPosition === 'top' ? 'max(20px, env(safe-area-inset-bottom))' : 0, 
            ...listContainerSx 
        }}>
          {banners && !appliedFilters && !appliedDateFilter && <Box sx={{ pt: 0, flexShrink: 0 }}>{banners}</Box>}
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: viewMode === 'table' ? 0 : 'auto' }}>
            {searchRequired && !hasSearchInput ? (
              // Chưa nhập điều kiện: nói rõ vì sao danh sách trống, thay vì để người dùng tưởng mất dữ liệu.
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, p: 5, textAlign: 'center' }}>
                <Typography variant="body1" fontWeight="600">Hãy chọn điều kiện để xem dữ liệu</Typography>
                <Typography variant="body2" color="text.secondary">
                  Màn hình này có lượng dữ liệu lớn nên chỉ tải theo điều kiện bạn chọn.
                </Typography>
                <Button variant="contained" onClick={openFilterOrSearch} sx={{ mt: 1, textTransform: 'none', borderRadius: 2 }}>
                  Chọn điều kiện
                </Button>
              </Box>
            ) : isLoadingList && allItems.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
            ) : isErrorList ? (
              <Typography color="error" textAlign="center">{error?.message || error?.error|| error || "Lỗi tải dữ liệu. Vui lòng thử lại."}</Typography>
            ) : (() => {
              const danhSach = (
                <DataListItems 
                   data={displayItems} 
                   totalRecords={totalRecords} 
                   fields={fields}
                   
                   loadingMore={isFetchingNextPage}
                   hasMore={hasNextPage}
                   onLoadMore={() => fetchNextPage()} 

                   currentLimit={currentLimit}
                   onLimitChange={setCurrentLimit}

                   canEdit={canEdit} canDelete={canDelete} 
                   canCopy={canAdd && canCopy} 
                   gridProps={gridProps}
                   viewMode={viewMode}
                   onEdit={handleOpenForm} onDelete={handleDelete} onView={handleOpenView} 
                   onCopy={handleCopy} 
                   renderItem={renderItem}
                   renderRowActions={coNutDong ? veNutDong : null}
                   customListScript={customListScript}

                   isSelectionMode={isSelectionMode}
                   selectedIds={selectedIds}
                   onSelectionChange={!hideHeader?setSelectedIds:null}

                   activeRowId={isSplitView ? activeRowId : undefined}
                   onRowClick={(isSplitView || typeof handleCellClicked === 'function') ? handleRowClick : undefined}

                   isEditableCell={inlineEditOn ? isEditableCell : undefined}
                   dirtyRowIds={dirtyIds}
                   savingRowId={savingRowId}
                   onCellChange={handleCellChange}
                   onCommitRow={handleCommitRow}
                   onCancelRow={handleCancelRow}
                />
              );
              if (!isSplitView) return danhSach;
              // Trên: bảng bản ghi. Dưới: chi tiết dòng đang chọn. Kéo thanh ngăn để chia lại.
              return (
                <SplitPane
                  ratio={splitRatio}
                  onRatioChange={setSplitRatio}
                  onRatioCommit={handleRatioCommit}
                  top={danhSach}
                  bottom={<RowDetailTabs row={activeRow} fields={fields} context={context} isMobile={isMobile} />}
                />
              );
            })()}
          </Box>
        </Box>

        {actionBarPosition === 'bottom' && !isSmUp && !hideHeader && (<Box sx={{ flexShrink: 0 }}>{renderToolbar(false)}</Box>)}

        <DataListFilter open={filterOpen} onClose={() => setFilterOpen(false)} fields={fields} dateField={dateField} currentFilters={appliedFilters} currentDateFilter={appliedDateFilter} onApplyFilter={handleApplyFilter} />
        {hasSearchForm && (
          <DataListSearchForm
            open={searchFormOpen}
            onClose={() => setSearchFormOpen(false)}
            fields={effectiveSearchFields}
            title={`Tìm kiếm ${title || ''}`.trim()}
            values={searchFormValues}
            onSearch={handleApplySearchForm}
            onClear={handleClearSearchForm}
            required={!!searchRequired && !hasSearchInput}
            masterData={masterData}
          />
        )}
        <DataListForm open={formOpen} onClose={handleCloseForm}  customFormScript={customFormScript} isEditing={isEditing} initialData={currentEditItem} fields={fields} saving={saveMutation.isPending} onSave={onSave || ((formData) => saveMutation.mutate(formData))} formSize={formSize} checkBeforeSave={checkBeforeSave} apiCode={finalApiCode} changeCodeForm={changeCodeForm} autoGroupMaster={autoGroupMaster} />

        {showPrintDialog && (
           <PrintActionDrawer 
            open={showPrintDialog} 
            onClose={() => setShowPrintDialog(false)} 
            code={code}
            listinfoCode={boundListinfoCode}
            apiCode={finalApiCode} mode="voucher" 
            selectedIds={selectedIds} 
            title={title} 
            columns={fields.filter(f => !f.hideInList && !['picture', 'color', 'hinh_anh'].includes(f.field))}
            filterFields={fields}
            searchCondition={{...appliedFilters}}
          />
        )}
      </Box>
      {guideUrl && guideOpen && (
        <React.Suspense fallback={null}>
          <HuongDanDialog open={guideOpen} onClose={() => setGuideOpen(false)} url={guideUrl} title={tieuDeHuongDan(title)} />
        </React.Suspense>
      )}
      {configEditor && (
        <React.Suspense fallback={null}>
          <DataListConfigEditor
            existing={configEditor.existing}
            prefill={configEditor.prefill}
            onClose={closeConfigEditor}
            onSaved={handleConfigSaved}
          />
        </React.Suspense>
      )}
      {canAdd && !isMobile && actionBarPosition !== 'bottom' && actionBarPosition !== 'top' && (<Fab color="primary" sx={{ position: 'absolute', bottom: 16, right: 16 }} onClick={() => handleOpenForm(null, getUrlPrefill())}><AddIcon /></Fab>)}
    </React.Fragment>
  );
});

// =====================================================================
// BỌC BẰNG REACT.MEMO
// =====================================================================
const DataListManagerMemo = React.memo(DataListManagerRender, (prevProps, nextProps) => {
  const prevSearch = getCleanSearchString(prevProps.location?.search);
  const nextSearch = getCleanSearchString(nextProps.location?.search);
  
  if (prevSearch !== nextSearch) return false;
  if (prevProps.location?.pathname !== nextProps.location?.pathname) return false;

  const prevKeys = Object.keys(prevProps);
  const nextKeys = Object.keys(nextProps);
  
  if (prevKeys.length !== nextKeys.length) return false;

  for (let key of prevKeys) {
    if (key === 'location') continue; 
    if (prevProps[key] !== nextProps[key]) return false;
  }

  return true;
});

// =====================================================================
// COMPONENT WRAPPER 
// =====================================================================
const DataListManager = forwardRef((props, ref) => {
  const location = useLocation();
  return <DataListManagerMemo {...props} location={location} ref={ref} />;
});

export default DataListManager;